import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import AgentLayout from '../../components/layout/AgentLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '../../components/ui/avatar';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Search,
  CheckCircle,
  Clock,
  AlertCircle,
  FileText,
  User,
  Eye,
  Target,
  GraduationCap,
  Plane,
  Home,
  DollarSign,
  Briefcase,
  RefreshCw,
  Loader2,
  Calendar,
  MoreVertical,
  Award,
  ArrowRight,
  AlertTriangle,
  Timer,
  LayoutGrid,
  List,
  ChevronRight,
  TrendingUp,
  Inbox,
  PlayCircle,
  PauseCircle,
  CheckCircle2,
  ArrowUpRight,
  MoreHorizontal,
  Filter,
  SlidersHorizontal,
} from 'lucide-react';
import { agentAPI } from '../../services/api';
import { useRoleBasePath } from '../../hooks/useRoleBasePath';
import { toast } from 'sonner';
import {
  format,
  formatDistanceToNow,
  isPast,
  isToday,
  isTomorrow,
  differenceInDays,
} from 'date-fns';
import { cn } from '../../lib/utils';

// Service type configuration
const SERVICE_TYPE_CONFIG = {
  PROFILE_ASSESSMENT: {
    label: 'Profile Assessment',
    shortLabel: 'Profile',
    icon: Target,
    color: 'text-blue-600',
    bg: 'bg-blue-50',
  },
  UNIVERSITY_SHORTLISTING: {
    label: 'Preapplication Support',
    shortLabel: 'Preapplication',
    icon: GraduationCap,
    color: 'text-violet-600',
    bg: 'bg-violet-50',
  },
  APPLICATION_ASSISTANCE: {
    label: 'Apply University',
    shortLabel: 'Apply Uni',
    icon: FileText,
    color: 'text-pink-600',
    bg: 'bg-pink-50',
  },
  VISA_GUIDANCE: {
    label: 'Visa & Interview Support',
    shortLabel: 'Visa',
    icon: Plane,
    color: 'text-orange-600',
    bg: 'bg-orange-50',
  },
  SCHOLARSHIP_SEARCH: {
    label: 'Ticket & Travel Support',
    shortLabel: 'Travel',
    icon: Award,
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
  },
  LOAN_ASSISTANCE: {
    label: 'Education Loan',
    shortLabel: 'Loan',
    icon: DollarSign,
    color: 'text-teal-600',
    bg: 'bg-teal-50',
  },
  ACCOMMODATION_HELP: {
    label: 'Find Accommodation',
    shortLabel: 'Accommodation',
    icon: Home,
    color: 'text-cyan-600',
    bg: 'bg-cyan-50',
  },
  PRE_DEPARTURE_ORIENTATION: {
    label: 'Find Jobs Abroad',
    shortLabel: 'Jobs Abroad',
    icon: Briefcase,
    color: 'text-indigo-600',
    bg: 'bg-indigo-50',
  },
};

// Status configuration for pipeline
const STATUS_CONFIG = {
  ASSIGNED: {
    label: 'New',
    fullLabel: 'Assigned',
    color: 'text-blue-700 bg-blue-50 border-blue-200',
    dot: 'bg-blue-500',
    icon: Inbox,
    // Column colors for Board view
    headerBg: 'bg-blue-600',
    headerText: 'text-white',
    headerBorder: 'border-blue-700',
    bodyBg: 'bg-blue-50',
    columnBorder: 'border-blue-200',
    countBg: 'bg-blue-500/20',
    countText: 'text-blue-100',
  },
  IN_PROGRESS: {
    label: 'Active',
    fullLabel: 'In Progress',
    color: 'text-amber-700 bg-amber-50 border-amber-200',
    dot: 'bg-amber-500',
    icon: PlayCircle,
    // Column colors for Board view
    headerBg: 'bg-amber-500',
    headerText: 'text-white',
    headerBorder: 'border-amber-600',
    bodyBg: 'bg-amber-50',
    columnBorder: 'border-amber-200',
    countBg: 'bg-amber-400/20',
    countText: 'text-amber-100',
  },
  WAITING_STUDENT: {
    label: 'Waiting',
    fullLabel: 'Waiting on Student',
    color: 'text-purple-700 bg-purple-50 border-purple-200',
    dot: 'bg-purple-500',
    icon: PauseCircle,
    // Column colors for Board view
    headerBg: 'bg-purple-600',
    headerText: 'text-white',
    headerBorder: 'border-purple-700',
    bodyBg: 'bg-purple-50',
    columnBorder: 'border-purple-200',
    countBg: 'bg-purple-500/20',
    countText: 'text-purple-100',
  },
  COMPLETED: {
    label: 'Closed',
    fullLabel: 'Completed',
    color: 'text-emerald-700 bg-emerald-50 border-emerald-200',
    dot: 'bg-emerald-500',
    icon: CheckCircle2,
    // Column colors for Board view
    headerBg: 'bg-emerald-600',
    headerText: 'text-white',
    headerBorder: 'border-emerald-700',
    bodyBg: 'bg-emerald-50',
    columnBorder: 'border-emerald-200',
    countBg: 'bg-emerald-500/20',
    countText: 'text-emerald-100',
  },
  ON_HOLD: {
    label: 'Hold',
    fullLabel: 'On Hold',
    color: 'text-slate-700 bg-slate-50 border-slate-200',
    dot: 'bg-slate-400',
    icon: PauseCircle,
    // Column colors for Board view
    headerBg: 'bg-slate-600',
    headerText: 'text-white',
    headerBorder: 'border-slate-700',
    bodyBg: 'bg-slate-100',
    columnBorder: 'border-slate-300',
    countBg: 'bg-slate-500/20',
    countText: 'text-slate-200',
  },
};

// Priority configuration
const PRIORITY_CONFIG = {
  LOW: {
    label: 'Low',
    color: 'text-slate-600 bg-slate-100',
    dot: 'bg-slate-400',
  },
  MEDIUM: {
    label: 'Medium',
    color: 'text-blue-600 bg-blue-100',
    dot: 'bg-blue-500',
  },
  HIGH: {
    label: 'High',
    color: 'text-orange-600 bg-orange-100',
    dot: 'bg-orange-500',
  },
  URGENT: {
    label: 'Urgent',
    color: 'text-red-600 bg-red-100',
    dot: 'bg-red-500',
  },
};

// Compact Case Card for Pipeline
const CaseCard = ({ caseData, onStatusChange }) => {
  const navigate = useNavigate();
  const basePath = useRoleBasePath();
  const serviceConfig = SERVICE_TYPE_CONFIG[caseData.serviceType] || {};
  const ServiceIcon = serviceConfig.icon || FileText;
  const priorityConfig =
    PRIORITY_CONFIG[caseData.priority] || PRIORITY_CONFIG.MEDIUM;

  const isOverdue =
    caseData.deadline &&
    isPast(new Date(caseData.deadline)) &&
    caseData.status !== 'COMPLETED';
  const isDueSoon =
    caseData.deadline &&
    !isOverdue &&
    differenceInDays(new Date(caseData.deadline), new Date()) <= 3;

  const handleOpenCase = () => {
    navigate(`${basePath}/service-requests/${caseData.serviceRequestId}`);
  };

  return (
    <div
      className={cn(
        'bg-white rounded-lg border p-3 cursor-pointer transition-all duration-200',
        'hover:shadow-md hover:border-slate-300',
        isOverdue && 'border-l-4 border-l-red-500',
        !isOverdue &&
          (caseData.priority === 'URGENT' || caseData.priority === 'HIGH') &&
          'border-l-4 border-l-orange-400',
      )}
      onClick={handleOpenCase}
    >
      {/* Top Row: Student + Actions */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <Avatar className="h-8 w-8 flex-shrink-0">
            <AvatarImage src={caseData.student?.avatar} />
            <AvatarFallback className="bg-slate-100 text-slate-600 text-xs font-medium">
              {caseData.student?.firstName?.[0]}
              {caseData.student?.lastName?.[0]}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="font-medium text-sm text-slate-900 truncate flex items-center gap-1">
              {caseData.student?.firstName} {caseData.student?.lastName}
              {caseData.isPartnerManaged && (
                <Badge className="bg-purple-100 text-purple-700 text-[9px] px-1 py-0">
                  Partner
                </Badge>
              )}
            </p>
            <p className="text-xs text-slate-500 truncate">
              {caseData.student?.email}
            </p>
            {caseData.isPartnerManaged && caseData.partner && (
              <p className="text-[10px] text-purple-700 truncate">
                via {caseData.partner.firstName} {caseData.partner.lastName}
              </p>
            )}
          </div>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={e => e.stopPropagation()}>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 flex-shrink-0"
            >
              <MoreHorizontal className="h-4 w-4 text-slate-400" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem
              onClick={e => {
                e.stopPropagation();
                handleOpenCase();
              }}
            >
              <Eye className="w-4 h-4 mr-2" />
              View Details
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {caseData.status !== 'IN_PROGRESS' && (
              <DropdownMenuItem
                onClick={e => {
                  e.stopPropagation();
                  onStatusChange(caseData, 'IN_PROGRESS');
                }}
              >
                <PlayCircle className="w-4 h-4 mr-2" />
                Start Working
              </DropdownMenuItem>
            )}
            {caseData.status !== 'WAITING_STUDENT' && (
              <DropdownMenuItem
                onClick={e => {
                  e.stopPropagation();
                  onStatusChange(caseData, 'WAITING_STUDENT');
                }}
              >
                <PauseCircle className="w-4 h-4 mr-2" />
                Mark Waiting
              </DropdownMenuItem>
            )}
            {caseData.status !== 'COMPLETED' && (
              <DropdownMenuItem
                onClick={e => {
                  e.stopPropagation();
                  onStatusChange(caseData, 'COMPLETED');
                }}
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Complete
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Service Type */}
      <div className="flex items-center gap-1.5 mb-2">
        <ServiceIcon className={cn('w-3.5 h-3.5', serviceConfig.color)} />
        <span className="text-xs font-medium text-slate-700">
          {serviceConfig.shortLabel}
        </span>
        {(caseData.priority === 'HIGH' || caseData.priority === 'URGENT') && (
          <span
            className={cn(
              'ml-auto text-[10px] font-semibold px-1.5 py-0.5 rounded',
              priorityConfig.color,
            )}
          >
            {priorityConfig.label}
          </span>
        )}
      </div>

      {/* Progress */}
      <div className="mb-2">
        <div className="flex items-center justify-between text-[10px] text-slate-500 mb-1">
          <span>Progress</span>
          <span className="font-medium text-slate-700">
            {caseData.progress || 0}%
          </span>
        </div>
        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div
            className={cn(
              'h-full rounded-full transition-all',
              caseData.progress >= 100
                ? 'bg-emerald-500'
                : caseData.progress >= 50
                  ? 'bg-blue-500'
                  : 'bg-slate-400',
            )}
            style={{ width: `${caseData.progress || 0}%` }}
          />
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between text-[10px]">
        {caseData.deadline ? (
          <span
            className={cn(
              'flex items-center gap-1',
              isOverdue
                ? 'text-red-600 font-medium'
                : isDueSoon
                  ? 'text-orange-600'
                  : 'text-slate-500',
            )}
          >
            <Calendar className="w-3 h-3" />
            {isOverdue
              ? 'Overdue'
              : format(new Date(caseData.deadline), 'MMM d')}
          </span>
        ) : (
          <span className="text-slate-400">No deadline</span>
        )}
        {caseData.taskStats && caseData.taskStats.total > 0 && (
          <span className="text-slate-500">
            {caseData.taskStats.completed}/{caseData.taskStats.total} tasks
          </span>
        )}
      </div>
    </div>
  );
};

// Pipeline Column
const PipelineColumn = ({ status, cases, onStatusChange, isLoading }) => {
  const config = STATUS_CONFIG[status];
  const StatusIcon = config.icon;

  return (
    <div
      className={cn(
        'flex flex-col w-[300px] min-w-[300px] rounded-lg border',
        config.columnBorder,
        config.bodyBg,
      )}
    >
      {/* Column Header */}
      <div
        className={cn(
          'flex items-center justify-between px-3 py-2.5 border-b rounded-t-lg',
          config.headerBg,
          config.headerBorder,
        )}
      >
        <div className="flex items-center gap-2">
          <StatusIcon className={cn('w-4 h-4', config.headerText)} />
          <span className={cn('font-semibold text-sm', config.headerText)}>
            {config.fullLabel}
          </span>
        </div>
        <span
          className={cn(
            'text-xs font-medium px-2 py-0.5 rounded-full',
            config.countBg,
            config.countText,
          )}
        >
          {cases.length}
        </span>
      </div>

      {/* Cases */}
      <div className="flex-1 p-2 space-y-2 overflow-y-auto max-h-[calc(100vh-340px)] min-h-[200px]">
        {cases.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-slate-400">
            <StatusIcon className="w-8 h-8 mb-2 opacity-40" />
            <p className="text-xs">No cases</p>
          </div>
        ) : (
          cases.map(caseData => (
            <CaseCard
              key={caseData.serviceRequestId}
              caseData={caseData}
              onStatusChange={onStatusChange}
            />
          ))
        )}
      </div>
    </div>
  );
};

// Table Row for List View
const CaseTableRow = ({ caseData, onStatusChange }) => {
  const navigate = useNavigate();
  const basePath = useRoleBasePath();
  const serviceConfig = SERVICE_TYPE_CONFIG[caseData.serviceType] || {};
  const ServiceIcon = serviceConfig.icon || FileText;
  const statusConfig = STATUS_CONFIG[caseData.status] || STATUS_CONFIG.ASSIGNED;
  const priorityConfig =
    PRIORITY_CONFIG[caseData.priority] || PRIORITY_CONFIG.MEDIUM;

  const isOverdue =
    caseData.deadline &&
    isPast(new Date(caseData.deadline)) &&
    caseData.status !== 'COMPLETED';

  return (
    <TableRow
      className="cursor-pointer hover:bg-slate-50"
      onClick={() =>
        navigate(`${basePath}/service-requests/${caseData.serviceRequestId}`)
      }
    >
      <TableCell>
        <div className="flex items-center gap-3">
          <Avatar className="h-8 w-8">
            <AvatarImage src={caseData.student?.avatar} />
            <AvatarFallback className="bg-slate-100 text-slate-600 text-xs">
              {caseData.student?.firstName?.[0]}
              {caseData.student?.lastName?.[0]}
            </AvatarFallback>
          </Avatar>
          <div>
            <p className="font-medium text-sm text-slate-900 flex items-center gap-1.5">
              {caseData.student?.firstName} {caseData.student?.lastName}
              {caseData.isPartnerManaged && (
                <Badge className="bg-purple-100 text-purple-700 text-[9px] px-1.5 py-0">
                  Partner
                </Badge>
              )}
            </p>
            <p className="text-xs text-slate-500">{caseData.student?.email}</p>
            {caseData.isPartnerManaged && caseData.partner && (
              <p className="text-[11px] text-purple-700 mt-0.5">
                via {caseData.partner.firstName} {caseData.partner.lastName}
              </p>
            )}
          </div>
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1.5">
          <ServiceIcon className={cn('w-4 h-4', serviceConfig.color)} />
          <span className="text-sm">{serviceConfig.shortLabel}</span>
        </div>
      </TableCell>
      <TableCell>
        <Badge
          variant="outline"
          className={cn('text-xs font-medium', statusConfig.color)}
        >
          <span
            className={cn('w-1.5 h-1.5 rounded-full mr-1.5', statusConfig.dot)}
          />
          {statusConfig.label}
        </Badge>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2 w-24">
          <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div
              className={cn(
                'h-full rounded-full',
                caseData.progress >= 100
                  ? 'bg-emerald-500'
                  : caseData.progress >= 50
                    ? 'bg-blue-500'
                    : 'bg-slate-400',
              )}
              style={{ width: `${caseData.progress || 0}%` }}
            />
          </div>
          <span className="text-xs text-slate-600 w-8">
            {caseData.progress || 0}%
          </span>
        </div>
      </TableCell>
      <TableCell>
        <span
          className={cn(
            'inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded font-medium',
            priorityConfig.color,
          )}
        >
          <span
            className={cn('w-1.5 h-1.5 rounded-full', priorityConfig.dot)}
          />
          {priorityConfig.label}
        </span>
      </TableCell>
      <TableCell>
        {caseData.deadline ? (
          <span
            className={cn(
              'text-sm',
              isOverdue ? 'text-red-600 font-medium' : 'text-slate-600',
            )}
          >
            {isOverdue && <AlertCircle className="w-3 h-3 inline mr-1" />}
            {format(new Date(caseData.deadline), 'MMM d, yyyy')}
          </span>
        ) : (
          <span className="text-slate-400 text-sm">—</span>
        )}
      </TableCell>
      <TableCell>
        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={e => e.stopPropagation()}>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={e => {
                e.stopPropagation();
                navigate(
                  `${basePath}/service-requests/${caseData.serviceRequestId}`,
                );
              }}
            >
              <Eye className="w-4 h-4 mr-2" />
              View Details
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {caseData.status !== 'IN_PROGRESS' && (
              <DropdownMenuItem
                onClick={e => {
                  e.stopPropagation();
                  onStatusChange(caseData, 'IN_PROGRESS');
                }}
              >
                <PlayCircle className="w-4 h-4 mr-2" />
                Start Working
              </DropdownMenuItem>
            )}
            {caseData.status !== 'COMPLETED' && (
              <DropdownMenuItem
                onClick={e => {
                  e.stopPropagation();
                  onStatusChange(caseData, 'COMPLETED');
                }}
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Mark Complete
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
};

export default function AgentServiceRequests() {
  const navigate = useNavigate();
  const basePath = useRoleBasePath();
  const [loading, setLoading] = useState(true);
  const [pipeline, setPipeline] = useState({});
  const [stats, setStats] = useState({});
  const [allCases, setAllCases] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const [activeFilter, setActiveFilter] = useState('all');
  const [serviceTypeFilter, setServiceTypeFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchPipeline();
  }, []);

  const fetchPipeline = async () => {
    try {
      setLoading(true);
      const response = await agentAPI.getCasePipeline();
      setPipeline(response.pipeline || {});
      setStats(response.stats || {});
      setAllCases(response.all || []);
    } catch (error) {
      console.error('Failed to fetch pipeline:', error);
      toast.error('Failed to load case pipeline');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (caseData, newStatus) => {
    try {
      setUpdating(true);
      await agentAPI.updateCaseStatus(caseData.serviceRequestId, newStatus);
      toast.success(`Case moved to ${STATUS_CONFIG[newStatus]?.fullLabel}`);
      fetchPipeline();
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update case status');
    } finally {
      setUpdating(false);
    }
  };

  // Filter cases based on search and filters
  const filteredPipeline = useMemo(() => {
    const filterCases = cases => {
      return cases.filter(c => {
        const studentName =
          `${c.student?.firstName || ''} ${c.student?.lastName || ''}`.toLowerCase();
        const serviceLabel =
          SERVICE_TYPE_CONFIG[c.serviceType]?.label?.toLowerCase() || '';
        const matchesSearch =
          !searchQuery ||
          studentName.includes(searchQuery.toLowerCase()) ||
          serviceLabel.includes(searchQuery.toLowerCase());

        const matchesServiceType =
          serviceTypeFilter === 'all' || c.serviceType === serviceTypeFilter;
        const matchesPriority =
          priorityFilter === 'all' || c.priority === priorityFilter;

        return matchesSearch && matchesServiceType && matchesPriority;
      });
    };

    return {
      ASSIGNED: filterCases(pipeline.ASSIGNED || []),
      IN_PROGRESS: filterCases(pipeline.IN_PROGRESS || []),
      WAITING_STUDENT: filterCases(pipeline.WAITING_STUDENT || []),
      COMPLETED: filterCases(pipeline.COMPLETED || []),
      ON_HOLD: filterCases(pipeline.ON_HOLD || []),
    };
  }, [pipeline, searchQuery, serviceTypeFilter, priorityFilter]);

  // Filter all cases for list view
  const filteredAllCases = useMemo(() => {
    return allCases.filter(c => {
      const studentName =
        `${c.student?.firstName || ''} ${c.student?.lastName || ''}`.toLowerCase();
      const serviceLabel =
        SERVICE_TYPE_CONFIG[c.serviceType]?.label?.toLowerCase() || '';
      const matchesSearch =
        !searchQuery ||
        studentName.includes(searchQuery.toLowerCase()) ||
        serviceLabel.includes(searchQuery.toLowerCase());

      const matchesServiceType =
        serviceTypeFilter === 'all' || c.serviceType === serviceTypeFilter;
      const matchesPriority =
        priorityFilter === 'all' || c.priority === priorityFilter;
      const matchesStatus = activeFilter === 'all' || c.status === activeFilter;

      return (
        matchesSearch && matchesServiceType && matchesPriority && matchesStatus
      );
    });
  }, [allCases, searchQuery, serviceTypeFilter, priorityFilter, activeFilter]);

  if (loading) {
    return (
      <AgentLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="text-center">
            <Loader2 className="w-8 h-8 text-slate-400 animate-spin mx-auto mb-3" />
            <p className="text-sm text-slate-500">Loading cases...</p>
          </div>
        </div>
      </AgentLayout>
    );
  }

  return (
    <AgentLayout>
      <div className="space-y-4" data-testid="agent-service-requests-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900">
              Service Requests
            </h1>
            <p className="text-sm text-slate-500">
              Manage and track all assigned cases
            </p>
          </div>
          <Button
            onClick={fetchPipeline}
            variant="outline"
            size="sm"
            disabled={loading}
            className="h-9"
          >
            <RefreshCw
              className={cn('w-4 h-4 mr-2', loading && 'animate-spin')}
            />
            Refresh
          </Button>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-6  gap-3">
          {[
            {
              label: 'Total',
              value: stats.total || 0,
              color: 'text-slate-700',
              bg: 'bg-red-50',
              border: 'border-red-300',
            },
            {
              label: 'New',
              value: stats.assigned || 0,
              color: 'text-blue-700',
              bg: 'bg-blue-50',
              border: 'border-blue-300',
            },
            {
              label: 'Active',
              value: stats.inProgress || 0,
              color: 'text-amber-700',
              bg: 'bg-amber-50',
              border: 'border-amber-300',
            },
            {
              label: 'Waiting',
              value: stats.waitingStudent || 0,
              color: 'text-purple-700',
              bg: 'bg-purple-50',
              border: 'border-purple-300',
            },
            {
              label: 'Overdue',
              value: stats.overdue || 0,
              color: 'text-red-700',
              bg: 'bg-pink-50',
              border: 'border-pink-300',
            },
            {
              label: 'Completed',
              value: stats.completed || 0,
              color: 'text-emerald-700',
              bg: 'bg-emerald-50',
              border: 'border-emerald-300',
            },
          ].map(stat => (
            <div
              key={stat.label}
              className={cn(
                'rounded-lg border px-4 py-3',
                stat.bg,
                stat.border,
              )}
            >
              <p className={cn('text-2xl font-bold', stat.color)}>
                {stat.value}
              </p>
              <p className="text-xs text-slate-500 mt-0.5">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Toolbar */}
        <div className="flex items-center justify-between gap-4 py-2">
          <div className="flex items-center gap-3 flex-1">
            {/* Search */}
            <div className="relative w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search students or services..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="pl-9 h-9 bg-white"
              />
            </div>

            {/* Filters */}
            <Select
              value={serviceTypeFilter}
              onValueChange={setServiceTypeFilter}
            >
              <SelectTrigger className="w-44 h-9 bg-white">
                <SelectValue placeholder="Service Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Services</SelectItem>
                {Object.entries(SERVICE_TYPE_CONFIG).map(([key, config]) => (
                  <SelectItem key={key} value={key}>
                    {config.shortLabel}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className="w-32 h-9 bg-white">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {Object.entries(PRIORITY_CONFIG).map(([key, config]) => (
                  <SelectItem key={key} value={key}>
                    {config.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {(searchQuery ||
              serviceTypeFilter !== 'all' ||
              priorityFilter !== 'all') && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchQuery('');
                  setServiceTypeFilter('all');
                  setPriorityFilter('all');
                }}
                className="text-slate-500 h-9"
              >
                Clear filters
              </Button>
            )}
          </div>

          {/* View Toggle */}
          <div className="flex items-center border rounded-lg bg-slate-100 p-0.5">
            <Button
              variant={viewMode === 'pipeline' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('pipeline')}
              className={cn(
                'h-8 px-3',
                viewMode === 'pipeline' ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-transparent hover:bg-slate-200',
              )}
            >
              <LayoutGrid className="w-4 h-4 mr-1.5" />
              Board
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              className={cn(
                'h-8 px-3',
                viewMode === 'list' ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-transparent hover:bg-slate-200',
              )}
            >
              <List className="w-4 h-4 mr-1.5" />
              Table
            </Button>
          </div>
        </div>

        {/* Pipeline View */}
        {viewMode === 'pipeline' && (
          <div className="overflow-x-auto pb-4 -mx-2 px-2">
            <div className="flex gap-3 min-w-max">
              <PipelineColumn
                status="ASSIGNED"
                cases={filteredPipeline.ASSIGNED}
                onStatusChange={handleStatusChange}
              />
              <PipelineColumn
                status="IN_PROGRESS"
                cases={filteredPipeline.IN_PROGRESS}
                onStatusChange={handleStatusChange}
              />
              <PipelineColumn
                status="WAITING_STUDENT"
                cases={filteredPipeline.WAITING_STUDENT}
                onStatusChange={handleStatusChange}
              />
              <PipelineColumn
                status="COMPLETED"
                cases={filteredPipeline.COMPLETED}
                onStatusChange={handleStatusChange}
              />
            </div>
          </div>
        )}

        {/* List/Table View */}
        {viewMode === 'list' && (
          <div className="space-y-3">
            {/* Status Tabs */}
            <div className="bg-slate-100 rounded-lg p-1 border border-slate-200 inline-flex gap-1">
              {[
                { value: 'all', label: 'All', count: allCases.length },
                { value: 'ASSIGNED', label: 'New', count: stats.assigned },
                {
                  value: 'IN_PROGRESS',
                  label: 'Active',
                  count: stats.inProgress,
                },
                {
                  value: 'WAITING_STUDENT',
                  label: 'Waiting',
                  count: stats.waitingStudent,
                },
                {
                  value: 'COMPLETED',
                  label: 'Completed',
                  count: stats.completed,
                },
              ].map(tab => (
                <button
                  key={tab.value}
                  onClick={() => setActiveFilter(tab.value)}
                  className={cn(
                    'px-4 py-2 text-sm font-medium rounded-md transition-all',
                    activeFilter === tab.value
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/50',
                  )}
                >
                  <span className="flex items-center gap-2">
                    {tab.label}
                    <span
                      className={cn(
                        'text-xs font-medium px-1.5 py-0.5 rounded-full min-w-[20px] text-center',
                        activeFilter === tab.value
                          ? 'bg-slate-900 text-white'
                          : 'bg-slate-300 text-slate-400',
                      )}
                    >
                      {tab.count}
                    </span>
                  </span>
                </button>
              ))}
            </div>

            {/* Table */}
            {filteredAllCases.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 bg-slate-50 rounded-lg border border-dashed border-slate-200">
                <FileText className="w-12 h-12 text-slate-300 mb-3" />
                <h3 className="text-sm font-medium text-slate-700 mb-1">
                  No cases found
                </h3>
                <p className="text-xs text-slate-500">
                  Try adjusting your filters
                </p>
              </div>
            ) : (
              <div className="border rounded-lg bg-white overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50 hover:bg-slate-50">
                      <TableHead className="font-semibold">Student</TableHead>
                      <TableHead className="font-semibold">Service</TableHead>
                      <TableHead className="font-semibold">Status</TableHead>
                      <TableHead className="font-semibold">Progress</TableHead>
                      <TableHead className="font-semibold">Priority</TableHead>
                      <TableHead className="font-semibold">Deadline</TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAllCases.map(caseData => (
                      <CaseTableRow
                        key={caseData.serviceRequestId}
                        caseData={caseData}
                        onStatusChange={handleStatusChange}
                      />
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}
      </div>
    </AgentLayout>
  );
}
